import { useState, useEffect, useMemo } from 'react';
import { Coordinates, CalculationMethod, PrayerTimes } from 'adhan';
import { Moon, Sun, Sunrise, Sunset } from 'lucide-react';

// Helper for Curve Y (Quadratic Bezier: M 0,65 Q 50,-10 100,65)
// y = 150t^2 - 150t + 65, where t = x/100
const getCurveY = (x: number) => {
  const t = x / 100;
  return 150 * t * t - 150 * t + 65;
};

// Helper to get days until Ramadan
const getDaysUntilRamadan = () => {
  // Approximate date for Ramadan 2026
  const ramadanStart = new Date('2026-02-17T00:00:00');
  const today = new Date();
  today.setHours(0,0,0,0);
  const diffTime = ramadanStart.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export function SunTracker() {
  const [now, setNow] = useState(new Date());
  // Default to Mecca (21.4225, 39.8262) to ensure UI always renders immediately
  const [coords, setCoords] = useState<Coordinates | null>(new Coordinates(21.4225, 39.8262));

  // 1. Get Location & Time
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setCoords(new Coordinates(pos.coords.latitude, pos.coords.longitude));
    });

    const timer = setInterval(() => setNow(new Date()), 1000 * 60); // Update every min
    return () => clearInterval(timer);
  }, []);

  // 2. Calculate Prayer Times
  const { prayers, sunPosition, gradient } = useMemo(() => {
    // Fallback if coords is still somehow null (shouldn't be with default)
    const effectiveCoords = coords || new Coordinates(21.4225, 39.8262); 

    const params = CalculationMethod.MuslimWorldLeague();
    const pt = new PrayerTimes(effectiveCoords, now, params);

    const list = [
      { id: 'fajr', label: 'Fajr', time: pt.fajr, icon: Moon },
      { id: 'sunrise', label: 'Sunrise', time: pt.sunrise, icon: Sunrise }, // Reference point
      { id: 'dhuhr', label: 'Dhuhr', time: pt.dhuhr, icon: Sun },
      { id: 'asr', label: 'Asr', time: pt.asr, icon: Sun },
      { id: 'maghrib', label: 'Maghrib', time: pt.maghrib, icon: Sunset },
      { id: 'isha', label: 'Isha', time: pt.isha, icon: Moon },
    ];

    // Calculate Sun Position (Segmented Interpolation)
    const nowTime = now.getTime();
    let sX = 90; // Default to start (Fajr)
    let visible = false;

    const f = pt.fajr.getTime();
    const d = pt.dhuhr.getTime();
    const a = pt.asr.getTime();
    const m = pt.maghrib.getTime();
    const i = pt.isha.getTime();

    if (nowTime >= f && nowTime <= i) {
        visible = true;
        if (nowTime < d) {
            // Fajr -> Dhuhr (90 -> 70)
            const p = (nowTime - f) / (d - f);
            sX = 90 - (p * 20);
        } else if (nowTime < a) {
            // Dhuhr -> Asr (70 -> 50)
            const p = (nowTime - d) / (a - d);
            sX = 70 - (p * 20);
        } else if (nowTime < m) {
            // Asr -> Maghrib (50 -> 30)
            const p = (nowTime - a) / (m - a);
            sX = 50 - (p * 20);
        } else {
            // Maghrib -> Isha (30 -> 10)
            const p = (nowTime - m) / (i - m);
            sX = 30 - (p * 20);
        }
    } else {
         // Out of bounds (Night)
         visible = false; 
         if (nowTime > i) sX = 10;
    }

    const sY = getCurveY(sX);
    
    // Gradient Logic
    let grad = 'bg-gradient-to-br from-blue-400 via-sky-300 to-blue-200'; // Default Day
    let phase = 'day';

    if (now < pt.sunrise) {
        grad = 'bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-800'; // Dawn
        phase = 'dawn';
    } else if (now < pt.asr) {
        grad = 'bg-gradient-to-br from-sky-400 via-amber-200 to-sky-300'; // Midday
        phase = 'midday';
    } else if (now < pt.maghrib) {
        grad = 'bg-gradient-to-br from-amber-400 via-orange-300 to-rose-300'; // Golden Hour
        phase = 'afternoon';
    } else if (now < pt.isha) {
        grad = 'bg-gradient-to-br from-indigo-950 via-purple-900 to-indigo-900'; // Sunset/Dusk
        phase = 'sunset';
    } else {
        grad = 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'; // Night
        phase = 'night';
    }

    return { 
        prayers: list, 
        sunPosition: { x: sX, y: sY, visible },
        currentPhase: phase,
        gradient: grad
    };
  }, [now, coords]);

  const ramadanDays = getDaysUntilRamadan();

  if (!coords && !prayers.length) return <div className="h-40 animate-pulse bg-slate-100/10 rounded-3xl" />;

  return (
    <div className={`relative w-full h-48 rounded-3xl overflow-hidden shadow-2xl transition-all duration-1000 ${gradient} group`}>
        {/* Top Left Info: Ramadan Countdown & Time */}
        <div className="absolute top-4 left-4 z-30 flex items-center gap-3">
            {ramadanDays > 0 && ramadanDays < 100 && (
                <div className="flex items-center gap-1.5 bg-emerald-50/90 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm">
                    <Moon className="w-3.5 h-3.5 text-emerald-600 fill-emerald-600/20" />
                    <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide">
                        {ramadanDays} Days
                    </span>
                </div>
            )}
            <span className="text-sm font-bold text-white font-mono drop-shadow-md">
                {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
        </div>

        {/* Ambient Particles (Stars/Dust) */}
        <div className="absolute inset-0 opacity-30">
            {[...Array(10)].map((_, i) => (
                <div 
                    key={i}
                    className="absolute bg-white rounded-full animate-pulse"
                    style={{
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        width: Math.random() * 3 + 'px',
                        height: Math.random() * 3 + 'px',
                        animationDelay: `${Math.random() * 5}s`,
                        animationDuration: `${3 + Math.random() * 5}s`
                    }}
                />
            ))}
        </div>

        {/* The Path (SVG Curve) - VISUAL ONLY */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
            <path 
                d="M 0,65 Q 50,-10 100,65" 
                fill="none" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeDasharray="4 4"
                strokeOpacity="0.4"
                vectorEffect="non-scaling-stroke"
            />
        </svg>
        
        {/* Dynamic Sun Indicator */}
        {sunPosition.visible && (
            <div 
                className="absolute w-8 h-8 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center transition-all duration-1000 ease-in-out"
                style={{ 
                    left: `${sunPosition.x}%`, 
                    top: `${sunPosition.y}%` 
                }}
            >
                <div className="w-4 h-4 bg-amber-400 rounded-full shadow-[0_0_15px_rgba(251,191,36,0.6)] animate-pulse" />
                <div className="absolute w-full h-full border border-amber-300/30 rounded-full animate-ping opacity-20" />
            </div>
        )}

        {/* Markers - WITH LINES */}
        {prayers.filter(p => p.id !== 'sunrise').map((p, i) => {
            // Map 5 prayers to fixed positions
            // i goes 0..4
            // RTL: 90% -> 10%
            const visualX = 90 - (i * 20); // 90, 70, 50, 30, 10
            
            // Map Y to curve (strictly following Bezier)
            const visualY = getCurveY(visualX); 

            return (
                <div 
                    key={p.id}
                    className="absolute flex flex-col items-center -translate-x-1/2 z-10"
                    style={{ 
                        left: `${visualX}%`, 
                        top: `${visualY}%`,
                        height: '100px' // Ensure container extends down for the line
                    }}
                >
                    {/* Dot on Curve */}
                    <div className="w-1.5 h-1.5 bg-slate-900 rounded-full shadow-sm mb-0.5" />
                    
                    {/* Vertical Line */}
                    <div className="w-px h-6 bg-gradient-to-b from-slate-900/60 to-transparent mb-1" />
                    
                    {/* Label & Time */}
                    <div className="text-center">
                        <span className="block text-[8px] font-black text-slate-900 tracking-widest uppercase drop-shadow-sm leading-tight">{p.label}</span>
                        <span className="block text-[8px] font-bold text-slate-800 font-mono drop-shadow-sm mt-0.5">
                            {p.time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                    </div>
                </div>
            );
        })}
    </div>
  );
}
